import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";

const schema = z
    .object({
        full_name: z.string().min(1, "Your full name").max(120),
        email: z.string().email("Enter a valid email"),
        password: z.string().min(8, "At least 8 characters"),
        confirm: z.string().min(1, "Confirm your password"),
    })
    .refine((d) => d.password === d.confirm, {
        message: "Passwords do not match",
        path: ["confirm"],
    });

export default function RegisterPage() {
    const { register: registerUser, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState(null);

    React.useEffect(() => {
        if (isAuthenticated) navigate("/", { replace: true });
    }, [isAuthenticated, navigate]);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({ resolver: zodResolver(schema) });

    const onSubmit = async (values) => {
        setSubmitting(true);
        setServerError(null);
        try {
            const user = await registerUser(values.email, values.password, values.full_name);
            toast.success(`Welcome to Happy Lab, ${user.full_name.split(" ")[0]}`);
            navigate("/", { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Registration failed.";
            setServerError(typeof msg === "string" ? msg : "Registration failed.");
            toast.error(typeof msg === "string" ? msg : "Registration failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-background grain-bg flex flex-col">
            <div className="flex flex-1 items-center justify-center px-4 py-12">
                <div className="w-full max-w-md animate-fade-in-up">
                    <div className="mb-8 flex justify-center">
                        <img
                            src="/happylab-logo-full.png"
                            alt="Happy Lab"
                            className="h-auto w-40 object-contain"
                            data-testid="auth-brand-logo"
                        />
                    </div>

                    <h1 className="text-3xl font-semibold tracking-tight text-foreground">Create your account</h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Start practising therapy conversations in a safe space.
                    </p>

                    <Card className="mt-8 border-border/80 shadow-sm">
                        <CardContent className="p-6">
                            <form
                                onSubmit={handleSubmit(onSubmit)}
                                className="space-y-5"
                                data-testid="register-form"
                            >
                                <div className="space-y-2">
                                    <Label htmlFor="full_name">Full name</Label>
                                    <Input
                                        id="full_name"
                                        autoComplete="name"
                                        placeholder="Your name"
                                        data-testid="register-fullname-input"
                                        {...register("full_name")}
                                    />
                                    {errors.full_name && (
                                        <p className="text-xs text-destructive">{errors.full_name.message}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        autoComplete="email"
                                        placeholder="you@example.com"
                                        data-testid="register-email-input"
                                        {...register("email")}
                                    />
                                    {errors.email && (
                                        <p className="text-xs text-destructive">{errors.email.message}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password">Password</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        autoComplete="new-password"
                                        placeholder="At least 8 characters"
                                        data-testid="register-password-input"
                                        {...register("password")}
                                    />
                                    {errors.password && (
                                        <p className="text-xs text-destructive">{errors.password.message}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirm">Confirm password</Label>
                                    <Input
                                        id="confirm"
                                        type="password"
                                        autoComplete="new-password"
                                        placeholder="Type it again"
                                        data-testid="register-confirm-input"
                                        {...register("confirm")}
                                    />
                                    {errors.confirm && (
                                        <p className="text-xs text-destructive">{errors.confirm.message}</p>
                                    )}
                                </div>

                                {serverError && (
                                    <div
                                        className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                                        data-testid="register-server-error"
                                    >
                                        {serverError}
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    size="lg"
                                    className="w-full bg-primary hover:bg-primary-hover"
                                    disabled={submitting}
                                    data-testid="register-submit-button"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Creating account…
                                        </>
                                    ) : (
                                        <>
                                            Create account
                                            <ArrowRight className="h-4 w-4" />
                                        </>
                                    )}
                                </Button>
                            </form>

                            <p className="mt-6 text-sm text-muted-foreground">
                                Already have an account?{" "}
                                <Link
                                    to="/login"
                                    className="font-medium text-primary hover:underline"
                                    data-testid="goto-login-link"
                                >
                                    Sign in
                                </Link>
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
